<!--
Author: Alexey Usov (dax@xdax.ru, https://github.com/doubleaxe)
Please don't remove this comment if you use unmodified file
-->
<script setup lang="ts">
import {mdiAlertCircleOutline, mdiClose, mdiContentPaste, mdiFileUploadOutline} from '@mdi/js';
import {useClipboard, useVModel} from '@vueuse/core';
import {injectBlueprintModel} from '@/scripts/model/store';
import {nextTick, ref, unref, watch} from 'vue';
import {useErrorHandler, loadBlueprint} from '@/composables';
import {injectGameData} from '@/scripts/data';

const props = defineProps<{
    modelValue: boolean;
}>();
const emit = defineEmits(['update:modelValue']);
const dialog = useVModel(props, 'modelValue', emit);

const gameData = injectGameData();
const blueprintModel = injectBlueprintModel();

const loadedBlueprint = ref('');
const loadError = ref(false);
const showInputFile = ref(false);
const inputFile = ref<HTMLElement | null>(null);
const fileName = ref('');

const {isSupported: isClipboardSupported} = useClipboard();
const {showError} = useErrorHandler();
const ERROR_TITLE = 'Error loading blueprint';

watch(() => props.modelValue, (value) => {
    if(value) {
        loadedBlueprint.value = '';
        loadError.value = false;
    }
});

watch(loadedBlueprint, () => {
    loadError.value = false;
});

function paste() {
    navigator.clipboard.readText().then((text) => {
        loadedBlueprint.value = text;
        fileName.value = '';
    });
}

function loadingBlueprint() {
    showInputFile.value = false;
    //recreate input
    nextTick(() => {
        showInputFile.value = true;
        nextTick(() => {
            unref(inputFile)?.click();
        });
    });
}

function loadBlueprintFromFile() {
    if(!unref(inputFile))
        return;
    const selectedFiles = (unref(inputFile) as HTMLInputElement).files;
    if(!selectedFiles || !selectedFiles[0])
        return;
    const selectedFile = selectedFiles[0];
    const reader = new FileReader();
    reader.onload = function() {
        const fileContents = reader.result;
        if(typeof(fileContents) != 'string') {
            const error = new Error(`Invalid file type, expected "string", got "${typeof(fileContents)}"`);
            showError(ERROR_TITLE, error);
            return;
        }
        fileName.value = selectedFile.name;
        load(fileContents);
    };
    reader.onerror = function() {
        showError(ERROR_TITLE, reader.error);
    };
    reader.readAsText(selectedFile);
}


function load(encodedBlueprint?: string) {
    if(!encodedBlueprint) {
        encodedBlueprint = unref(loadedBlueprint);
    }
    //on failure stay open and say so inline (loadBlueprint's global error
    //dialog is suppressed here) — never close silently over a bad paste
    const loaded = loadBlueprint(gameData, blueprintModel, encodedBlueprint, fileName, true);
    if(loaded) {
        dialog.value = false;
    } else {
        loadError.value = true;
    }
}
</script>

<template>
    <v-dialog v-model="dialog">
        <v-sheet>
            <v-toolbar>
                <v-toolbar-title>Load Blueprint</v-toolbar-title>
                <v-spacer />
                <v-btn
                    :icon="mdiClose"
                    @click="dialog = false"
                />
            </v-toolbar>
            <v-container>
                <div class="my-flex-xs-wrap mb-4">
                    <div class="flex-grow-1 mr-1 mt-2">
                        <v-textarea
                            v-model="loadedBlueprint"
                            class="text-monospaced textarea-overflow-auto"
                            label="Paste Blueprint Data (planner or in-game)"
                            variant="outlined"
                            hide-details
                        />
                    </div>
                    <div class="mt-2">
                        <v-btn
                            v-if="isClipboardSupported"
                            block
                            size="small"
                            class="mb-1"
                            :prepend-icon="mdiContentPaste"
                            @click="paste()"
                        >
                            Paste From Clipboard
                        </v-btn>
                        <v-btn
                            block
                            size="small"
                            color="secondary"
                            :prepend-icon="mdiFileUploadOutline"
                            @click="loadingBlueprint()"
                        >
                            Load From File
                        </v-btn>
                        <input
                            v-if="showInputFile"
                            ref="inputFile"
                            type="file"
                            class="d-none"
                            accept=".txt,text/plain"
                            @change="loadBlueprintFromFile()"
                        >
                    </div>
                </div>
                <v-alert
                    v-if="loadError"
                    type="error"
                    variant="tonal"
                    density="comfortable"
                    class="mb-4"
                    :icon="mdiAlertCircleOutline"
                >
                    That text doesn't contain a recognisable blueprint. Planner plans contain a section starting with "DAXFB"; in-game Captains of Industry blueprints start like "B1234:".
                    A blueprint folder (starting "F") can't be imported — paste a single blueprint instead.
                </v-alert>
                <v-row dense>
                    <v-btn
                        color="primary"
                        block
                        @click="load()"
                    >
                        Load Blueprint
                    </v-btn>
                </v-row>
            </v-container>
        </v-sheet>
    </v-dialog>
</template>
